import { ITagResult } from '~src/httpClient/serverApi/types/IResponse';
import { ITagError } from '~src/httpClient/serverApi/types/ITagError';

import { IProcessedViewMonitoringStatusInfo, IRawViewMonitoringStatusInfo } from '../../types/AuthorConfiguredDCRTypes';
import { convertToProcessedMonitoringStatus, convertToProcessedShouldMonitor } from '../../utils/AuthorConfiguredDCRUtils';
import { ITagApiRequestConfig, makeTagApiRequest } from '../TagApiClient';

// Limit set by the getMonitorStatusForViews API. Most likely won't be triggered as PAGESIZE_SLICE and PAGESIZE_INITIAL_LOAD are smaller
// https://gitlab.tableausoftware.com/browser-clients/vizportalclient/-/blob/master/src/spacesPlatform/sagas/content/MixedContentSagas.ts#L218
const MAX_REQUESTED_AUTHOR_CONFIGURED_VIEWS = 1000;

export interface IGetMonitorStatusForViewsParams {
  view_luids: string[];
}

export interface IRawGetMonitorStatusForViewsResult extends ITagResult {
  // Made it optional for potential error response
  view_monitoring_statuses?: IRawViewMonitoringStatusInfo[];
}

export interface IProcessedGetMonitorStatusForViewsResult {
  viewMonitoringStatusesInfo: IProcessedViewMonitoringStatusInfo[];
  errors?: ITagError[];
}

function getViewLuidsSlices(viewLuids: string[]): string[][] {
  const viewLuidsSlices: string[][] = [];
  for (let start = 0; start < viewLuids.length; start += MAX_REQUESTED_AUTHOR_CONFIGURED_VIEWS) {
    const currentSlice = viewLuids.slice(start, start + MAX_REQUESTED_AUTHOR_CONFIGURED_VIEWS);
    viewLuidsSlices.push(currentSlice);
  }
  return viewLuidsSlices;
}

export async function getMonitorStatusForViews(params: IGetMonitorStatusForViewsParams): Promise<IProcessedGetMonitorStatusForViewsResult> {
  const viewLuidsSlices = getViewLuidsSlices(params.view_luids);

  const requests = viewLuidsSlices.map(async (viewLuids) => {
    const config: ITagApiRequestConfig<IGetMonitorStatusForViewsParams, {}> = {
      httpMethod: 'POST',
      endpoint: 'services/datamonitoring/monitoredViews/monitorStatus',
      requestBody: {
        view_luids: viewLuids
      }
    };

    const { view_monitoring_statuses, errors } = await makeTagApiRequest<IRawGetMonitorStatusForViewsResult, IGetMonitorStatusForViewsParams, {}>(config);
    const processedResult: IProcessedGetMonitorStatusForViewsResult = {
      viewMonitoringStatusesInfo: (view_monitoring_statuses || []).map(viewMonitoringStatusInfo => ({
        viewLuid: viewMonitoringStatusInfo.view_luid,
        shouldMonitor: convertToProcessedShouldMonitor(viewMonitoringStatusInfo.has_should_monitor, viewMonitoringStatusInfo.should_monitor),
        status: convertToProcessedMonitoringStatus(viewMonitoringStatusInfo.status)
      }))
    };
    if (errors) {
      processedResult.errors = errors;
    }
    return processedResult;
  });

  // Have to use Promise.all here as Promise.allSettled is not supported by the current compiler of this project
  // Therefore, we don't accept partial resolved results
  const results = await Promise.all(requests);

  // Merge results of each slice
  const viewMonitoringStatusesInfo: IProcessedViewMonitoringStatusInfo[] = [];
  const errors: ITagError[] = [];
  for (const resultOfSlice of results) {
    viewMonitoringStatusesInfo.push(...resultOfSlice.viewMonitoringStatusesInfo);
    errors.push(...resultOfSlice.errors);
  }
  const processedResult: IProcessedGetMonitorStatusForViewsResult = {
    viewMonitoringStatusesInfo
  };
  if (errors.length) {
    processedResult.errors = errors;
  }
  return processedResult;
}
